/**
 * Registry → Features tab.
 *
 * Feature registry management for administrators.
 */

import { useState, type ReactElement } from 'react';
import {
  useAdminFeatures,
  useCreateFeature,
  useUpdateFeature,
  useSyncFeatures,
} from '@features/devcontainer/hooks/useAdminFeatures';
import { BaseImageInfoCard } from '@features/devcontainer/components/admin/BaseImageInfoCard';
import { FeatureTable } from '@features/devcontainer/components/admin/FeatureTable';
import { FeatureFormDialog } from '@features/devcontainer/components/admin/FeatureFormDialog';
import type { Feature, SyncResult } from '@features/devcontainer/schemas/admin.schemas';
import { formatSyncResult } from '@features/devcontainer/utils/syncResultMessage';

export function FeaturesTab(): ReactElement {
  const { data: features, isLoading, error } = useAdminFeatures(true);
  const createFeature = useCreateFeature();
  const updateFeature = useUpdateFeature();
  const syncFeatures = useSyncFeatures();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const handleSync = (): void => {
    setSyncMessage(null);
    syncFeatures.mutate(undefined, {
      onSuccess: (result: SyncResult) => { setSyncMessage(formatSyncResult(result)); },
    });
  };

  const handleEdit = (feature: Feature): void => {
    setSelectedFeature(feature);
    setDialogOpen(true);
  };

  const handleCreate = (): void => {
    setSelectedFeature(null);
    setDialogOpen(true);
  };

  const handleToggleActive = (feature: Feature): void => {
    updateFeature.mutate({
      featureId: feature.id,
      updates: { is_active: !feature.is_active },
    });
  };

  const handleSubmit = (
    data: Omit<Feature, 'id' | 'created_at' | 'updated_at' | 'is_active'>,
  ): void => {
    if (selectedFeature) {
      updateFeature.mutate(
        { featureId: selectedFeature.id, updates: data },
        { onSuccess: () => { setDialogOpen(false); } },
      );
    } else {
      createFeature.mutate(data, {
        onSuccess: () => { setDialogOpen(false); },
      });
    }
  };

  if (isLoading) {
    return <div className="settings-loading">Loading features...</div>;
  }

  if (error) {
    return (
      <div className="settings-error">
        Failed to load features. Please refresh the page.
      </div>
    );
  }

  return (
    <>
      <div className="admin-page__header">
        <button
          type="button"
          className="admin-page__sync-btn"
          onClick={handleSync}
          disabled={syncFeatures.isPending}
        >
          {syncFeatures.isPending ? 'Syncing...' : 'Sync from Registry'}
        </button>
        <button
          type="button"
          className="admin-page__create-btn"
          onClick={handleCreate}
        >
          Create Feature
        </button>
      </div>
      {syncMessage != null && (
        <div className="admin-page__sync-message admin-page__sync-message--report" role="status">
          {syncMessage}
        </div>
      )}

      <BaseImageInfoCard />

      <FeatureTable
        features={features ?? []}
        onEdit={handleEdit}
        onToggleActive={handleToggleActive}
      />

      <FeatureFormDialog
        open={dialogOpen}
        feature={selectedFeature}
        onClose={() => { setDialogOpen(false); }}
        onSubmit={handleSubmit}
        isSubmitting={
          createFeature.isPending || updateFeature.isPending
        }
      />
    </>
  );
}
